import { clsx } from "clsx";
import type { FC } from "react";
import { memo, useEffect, useRef, useState } from "react";
import { useHover } from "react-aria";
import { useDebouncedCallback } from "use-debounce";

import { LoadingSpinner } from "widgets";

import styleTrack from "../../genome-viewer/styles/Track.module.scss";

import { ShowMessageOverTrack } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { TrackInteractiveSurface } from "../TrackInteractiveSurface.js";
import { useTrackRefSize } from "../hooks/useTrackSize.js";
import { type TrackRange } from "../new/useViewerGraphql.js";
import { useTrackSequenceLazyQuery } from "./trackSequence.gql.js";
import { useError } from "@/app-utils/graphql/useError.ts";
import { getRangeLength, rangeContainsRange } from "@/app-utils/range.ts";
import type { Range } from "@/graphql.ts";
import { SEQ_RESIDUE_NU_COLORS } from "@/settings/settings.ts";

interface SequenceTrackProps {
  trackName?: string | null;
  contigId: number;
  range: TrackRange;
  headerWidth?: number;
  setRange?: (range: TrackRange) => void;
  setZoomRange?: (range: TrackRange | null) => void;
  setSelectionRange?: (range: TrackRange | null) => void;
}

const MIN_PIXEL_PER_BASE = 1;
const MIN_PIXEL_PER_LETTER = 7;
const BOX_HEIGHT = 14;

const useLoadSequence = (
  contigId: number,
  range: Range,
  baseWidth: number,
): { sequence; loading; error; contigLength } => {
  const [getSequence, { data, loading, error }] = useTrackSequenceLazyQuery();
  void useError(error);

  const [visibleSequence, setVisibleSequence] = useState<string>();

  const debouncedLoad = useDebouncedCallback(() => {
    void getSequence({
      variables: { contigId: contigId, rangeStart: range.start, rangeStop: range.stop },
    });
  }, 100);

  useEffect(() => {
    const missingData =
      !data ||
      data?.genomeViewer.trackSequence.contig.id !== contigId ||
      !rangeContainsRange(data?.genomeViewer.trackSequence.range, range);

    if (!(baseWidth < MIN_PIXEL_PER_BASE) && missingData) {
      debouncedLoad();
    }
  }, [baseWidth, debouncedLoad, range, contigId]);

  useEffect(() => {
    const d = data?.genomeViewer.trackSequence?.sequence.substr(
      range.start - data?.genomeViewer.trackSequence.range?.start,
      getRangeLength(range),
    );
    setVisibleSequence(d);
  }, [range, data]);

  return {
    sequence: visibleSequence,
    loading,
    error: error?.message,
    contigLength: data?.genomeViewer.trackSequence.contig.length,
  };
};

const SequenceTrack: FC<SequenceTrackProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { normalize, baseWidth } = useTrackRefSize(ref, props.range);
  const { hoverProps, isHovered } = useHover({});
  const tooSmall = baseWidth < MIN_PIXEL_PER_BASE;

  const { sequence, loading, error, contigLength } = useLoadSequence(props.contigId, props.range, baseWidth);

  return (
    <div
      className={clsx(styleTrack.track, styleTrack.track_no_header, { [styleTrack.track_hovered]: isHovered })}
      {...hoverProps}
      style={{ userSelect: "none" }}
      data-testid="sequence-track"
    >
      <TrackHeader isTrackHovered={isHovered} title={props.trackName ?? "Sequence"} headerWidth={props.headerWidth} />
      <TrackInteractiveSurface
        range={props.range}
        contigLength={contigLength}
        setRange={props.setRange}
        setZoomRange={props.setZoomRange}
        setSelectionRange={props.setSelectionRange}
        className={styleTrack.container}
        ref={ref}
        style={{ height: BOX_HEIGHT + 20 }}
      >
        {loading ? <LoadingSpinner debounceStart={1000} size={20} inCenter={true} /> : false}

        <svg width="100%" height={BOX_HEIGHT} style={{ display: "flex", alignContent: "flex-end" }}>
          {tooSmall || error ? (
            <foreignObject x="0" y="0" width={"100%"} height={BOX_HEIGHT}>
              <ShowMessageOverTrack message={error ?? "Zoom in to see sequence"} />
            </foreignObject>
          ) : (
            sequence?.split("")?.map((base: string, index) => {
              const xPos = Math.round(normalize(props.range.start - 1 + index)); // left border of the nucleotide box
              const boxWidth = baseWidth + 1; // add 1 to let it overlap into the next box. That avoids gaps when only the boxes are visible.
              return (
                <g key={`nuc-box-${index}`}>
                  <rect
                    id={`nuc-rect-${index}`}
                    x={xPos}
                    y={-1}
                    width={boxWidth}
                    height={BOX_HEIGHT}
                    fill={SEQ_RESIDUE_NU_COLORS[base.toLowerCase()]}
                  />
                  {baseWidth > MIN_PIXEL_PER_LETTER && (
                    <text
                      key={`nuc-letter-${xPos}`}
                      className={styleTrack.nucleotide_letter_svg}
                      textAnchor="middle"
                      x={xPos + baseWidth / 2}
                      y={1}
                      dy="1em"
                    >
                      {base}
                    </text>
                  )}
                </g>
              );
            })
          )}
        </svg>
      </TrackInteractiveSurface>
    </div>
  );
};

const MemoSequenceTrack = memo(SequenceTrack);
export { MemoSequenceTrack as SequenceTrack };
